import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { ButtonType, showAlert, showError } from '../../../../_metronic/helpers'
import { getPublicPrescricao } from '../prescricao/core/_request'
import { UsePrescricaoModal } from '../prescricao/components/modal/UsePrescricaoModal'
import { Button, FormInput, LoadingIndicator, RodapeConfiancaReceita } from '../../../components'

type Credenciais = { token: string; codigo: string }

/**
 * Página pública de dispensação (/dispensacao).
 * Etapa 1: farmacêutico informa token de acesso + código de desbloqueio.
 *          Validamos a prescrição pelo MESMO endpoint público do /prescricao
 *          (sem backend novo) — recusando receita expirada ou já dispensada.
 * Etapa 2: mostra o formulário do farmacêutico (reusa UsePrescricaoModal inline),
 *          que registra a baixa via POST /public/prescricao/utiliza.
 */
export const DispensacaoContent = () => {
  const { tokenPrescricao } = useParams<{ tokenPrescricao?: string }>()
  const [credenciais, setCredenciais] = useState<Credenciais | null>(null)

  const formik = useFormik({
    initialValues: {
      tokenPrescricao: tokenPrescricao || '',
      tokenPaciente: '',
    },
    validationSchema: Yup.object().shape({
      tokenPrescricao: Yup.string().required('Token de acesso é obrigatório'),
      tokenPaciente: Yup.string().required('Código de desbloqueio é obrigatório'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const prescricao = await getPublicPrescricao(values.tokenPrescricao, values.tokenPaciente)

        if (!prescricao.valida) {
          showAlert({ title: 'Erro!', text: 'Receita expirada ou inválida.', icon: 'error' })
          return
        }

        if (prescricao.utilizada) {
          showAlert({
            title: 'Receita já dispensada!',
            text: 'Esta prescrição já teve a dispensação registrada.',
            icon: 'error',
          })
          return
        }

        setCredenciais({ token: values.tokenPrescricao, codigo: values.tokenPaciente })
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <div className='w-100'>
      <div className='text-start mb-10'>
        <h1 className='mb-3'>Registrar dispensação</h1>
        <p className='text-muted'>
          {credenciais
            ? 'Informe os dados do farmacêutico para registrar a baixa do medicamento.'
            : 'Acesse a prescrição para registrar a dispensação do medicamento.'}
        </p>
      </div>

      {credenciais ? (
        <UsePrescricaoModal
          tokenPrescricao={credenciais.token}
          tokenPaciente={credenciais.codigo}
          onSuccess={() => {
            setCredenciais(null)
            formik.resetForm()
          }}
        />
      ) : (
        <form className='w-100' onSubmit={formik.handleSubmit} noValidate>
          <FormInput
            formik={formik}
            label='Token de acesso'
            placeholder='Digite o token de acesso'
            name='tokenPrescricao'
            className='mb-8'
            isRequired
            infoText='Token presente na prescrição'
          />
          <FormInput
            formik={formik}
            label='Código de desbloqueio'
            placeholder='Digite o código de desbloqueio'
            name='tokenPaciente'
            className='mb-8'
            isRequired
            infoText='4 primeiros dígitos do CPF do paciente'
          />
          <Button
            type={ButtonType.Submit}
            className='btn-primary btn-auth-full'
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Continuar'}
          </Button>
        </form>
      )}

      <RodapeConfiancaReceita />
    </div>
  )
}
